// Fixed-height window math for the task inbox's native <table>.
//
// The DataTable seam owns the table/caption/header; this helper owns only the
// pixel math for task rows. Constants are exported so the scroll region's CSS
// custom properties and the tests stay aligned with the rendered row geometry.

/** Pinned row height for every rendered task <tr>, including its bottom rule. */
export const TASK_TABLE_ROW_HEIGHT = 56;
/** Pinned sticky table-header height inside the task scroll region. */
export const TASK_TABLE_HEADER_HEIGHT = 40;
/** Fixed viewport keeps the number of mounted task rows predictably bounded. */
export const TASK_TABLE_VIEWPORT_HEIGHT = 560;
/** Extra rows above and below the visible slice for smooth ordinary scrolling. */
export const TASK_TABLE_OVERSCAN = 8;
/** Narrow viewports scroll horizontally instead of wrapping a fixed-height row. */
export const TASK_TABLE_MIN_INLINE_SIZE = 880;

export interface TaskTableWindow {
  readonly startIndex: number;
  readonly endIndex: number;
  readonly topSpacerHeight: number;
  readonly bottomSpacerHeight: number;
}

export interface TaskTableScrollArgs {
  readonly rowCount: number;
  readonly scrollTop: number;
  readonly rowHeight?: number;
  readonly headerHeight?: number;
  readonly viewportHeight?: number;
}

function maxScrollTop({
  rowCount,
  rowHeight = TASK_TABLE_ROW_HEIGHT,
  headerHeight = TASK_TABLE_HEADER_HEIGHT,
  viewportHeight = TASK_TABLE_VIEWPORT_HEIGHT,
}: Omit<TaskTableScrollArgs, "scrollTop">): number {
  return Math.max(0, headerHeight + rowCount * rowHeight - viewportHeight);
}

/** Keep a stale DOM scroll offset inside the current virtual table geometry. */
export function clampTaskTableScrollTop(args: TaskTableScrollArgs): number {
  const safeScrollTop = Number.isFinite(args.scrollTop) ? args.scrollTop : 0;
  return Math.min(Math.max(0, safeScrollTop), maxScrollTop(args));
}

/**
 * Scroll offset that puts an indexed task row immediately below the sticky
 * header. The header already occupies the row's natural table offset, so it
 * must not be added a second time here.
 */
export function getTaskTableFocusScrollTop(rowIndex: number): number {
  return Math.max(0, rowIndex) * TASK_TABLE_ROW_HEIGHT;
}

/**
 * Slice a native table body into a bounded window. The scroll container also
 * contains a sticky header, so only the body portion below that header counts
 * as visible task-row space.
 */
export function getTaskTableWindow({
  rowCount,
  scrollTop,
  rowHeight = TASK_TABLE_ROW_HEIGHT,
  headerHeight = TASK_TABLE_HEADER_HEIGHT,
  viewportHeight = TASK_TABLE_VIEWPORT_HEIGHT,
}: TaskTableScrollArgs): TaskTableWindow {
  const clampedScrollTop = clampTaskTableScrollTop({
    rowCount,
    scrollTop,
    rowHeight,
    headerHeight,
    viewportHeight,
  });
  const bodyScrollTop = Math.max(0, clampedScrollTop - headerHeight);
  const bodyViewportHeight = Math.max(0, viewportHeight - headerHeight);
  const firstVisibleIndex = Math.floor(bodyScrollTop / rowHeight);
  const startIndex = Math.max(0, firstVisibleIndex - TASK_TABLE_OVERSCAN);
  const endIndex = Math.min(
    rowCount,
    Math.ceil((bodyScrollTop + bodyViewportHeight) / rowHeight) + TASK_TABLE_OVERSCAN,
  );

  return {
    startIndex,
    endIndex,
    topSpacerHeight: startIndex * rowHeight,
    bottomSpacerHeight: (rowCount - endIndex) * rowHeight,
  };
}
